import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { getShowById, getRelatedShows, categories } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 py-3">
      <ol className="flex items-center flex-wrap gap-1 text-xs text-foreground-500">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && (
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            )}
            {item.href ? (
              <Link to={item.href} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground-700">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function DetailSkeleton() {
  return (
    <div className="w-full px-4 md:px-6 py-14 text-center">
      <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200 text-foreground-400">
        <i className="ri-error-warning-line text-2xl"></i>
      </span>
      <p className="text-foreground-500">未找到该剧集</p>
      <Link
        to="/"
        className="mt-4 inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
      >
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
        返回首页
      </Link>
    </div>
  );
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const [show, setShow] = useState<Show | undefined>();
  const [related, setRelated] = useState<Show[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'episodes'>('overview');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      const numId = parseInt(id, 10);
      const found = getShowById(numId);
      setShow(found);
      if (found) {
        setRelated(getRelatedShows(found));
      }
    }
  }, [id]);

  if (!show) {
    return (
      <>
        <Navbar />
        <main className="pt-16"><DetailSkeleton /></main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === show.categorySlug);

  const detailMetaTitle = `${show.title} - 天狼影院${show.title}全集高清免费在线观看`;
  const detailMetaDesc = `天狼影院提供${show.title}全集连播，高清免费在线观看，流畅不缓冲。${show.title}简介：${show.description.slice(0, 100)}`;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: show.category, href: `/category/${show.categorySlug}` },
            { label: show.title },
          ]}
        />

        {/* Detail header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12" aria-label="剧集详情">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Cover */}
            <div className="shrink-0 w-full sm:w-60 lg:w-72">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={show.cover}
                  alt={`${show.title} - 天狼影院高清在线观看`}
                  title={`${show.title} - 天狼影院高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {show.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {show.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                  {show.rating} 分
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mb-5 text-sm">
                <div>
                  <span className="text-foreground-500">导演：</span>
                  <span className="text-foreground-800">{show.director}</span>
                </div>
                <div>
                  <span className="text-foreground-500">主演：</span>
                  <span className="text-foreground-800">{show.actors.join('、')}</span>
                </div>
                <div>
                  <span className="text-foreground-500">地区：</span>
                  <span className="text-foreground-800">{show.region}</span>
                </div>
                <div>
                  <span className="text-foreground-500">语言：</span>
                  <span className="text-foreground-800">{show.language}</span>
                </div>
                <div>
                  <span className="text-foreground-500">年份：</span>
                  <span className="text-foreground-800">{show.year}</span>
                </div>
                <div>
                  <span className="text-foreground-500">状态：</span>
                  <span className="text-accent-600 font-medium">{show.updateInfo}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/detail/${show.id}`}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-play-fill"></i></span>
                  立即观看
                </Link>
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-heart-line"></i></span>
                  收藏
                </button>
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-share-line"></i></span>
                  分享
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Tab navigation */}
        <section className="w-full px-4 md:px-6 border-b border-background-200/60" aria-label="内容切换">
          <div className="flex items-center gap-1">
            {[
              { key: 'overview' as const, label: '剧集简介' },
              { key: 'episodes' as const, label: `分集列表 (${show.episodeCount})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer border-b-2 ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {/* Tab content */}
        <section className="w-full px-4 md:px-6 py-8" aria-label={activeTab === 'overview' ? '剧集简介' : '分集列表'}>
          {activeTab === 'overview' && (
            <div className="max-w-3xl">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {show.description}
              </p>
            </div>
          )}

          {activeTab === 'episodes' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {show.episodes.map((ep) => (
                <div
                  key={ep.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer group"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-semibold shrink-0">
                    {ep.id}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {ep.title}
                    </p>
                    <p className="text-xs text-foreground-500">{ep.duration}</p>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-400 shrink-0">
                    <i className="ri-play-circle-line"></i>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Related shows */}
        {related.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="相关推荐">
            <div className="flex items-center justify-between mb-5 md:mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">相关推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {related.map((r) => (
                <VideoCard key={r.id} show={r} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${show.title} - 天狼影院高清在线观看`,
            description: show.description,
            author: { '@type': 'Person', name: show.director },
            datePublished: `${show.year}-01-01`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tianlangcinema.com/' },
                { '@type': 'ListItem', position: 2, name: show.category, item: `https://tianlangcinema.com/category/${show.categorySlug}` },
                { '@type': 'ListItem', position: 3, name: show.title },
              ],
            },
          }),
        }}
      />
    </>
  );
}